import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Container,
  Alert,
  Paper,
  Divider,
  Avatar,
  ListItemAvatar
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase';
import { collection, getDocs } from 'firebase/firestore';

function OrganisationSelector() {
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { setEmulatedOrganisation } = useAuth();

  useEffect(() => {
    fetchOrganisations();
  }, []);

  const fetchOrganisations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get all documents from the /users collection
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      
      const orgs = snapshot.docs.map(snapshot => {
        const name = snapshot.data().name;
        return {
          id: snapshot.id,
          name: typeof name === 'string' && name.trim() ? name.trim() : snapshot.id,
        };
      });
      
      // Sort organizations alphabetically by name
      orgs.sort((a, b) => a.name.localeCompare(b.name));
      
      setOrganisations(orgs);
    } catch (err) {
      console.error('Error fetching organisations:', err);
      setError('Failed to load organisations. Please refresh the page and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOrganisation = (orgId) => {
    setEmulatedOrganisation(orgId);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#f0f2f5'
        }}
      >
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f0f2f5',
        display: 'flex',
        alignItems: 'center',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Card
          sx={{
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              p: 4,
              textAlign: 'center'
            }}
          >
            <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
              Select Organisation
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Choose an organisation to manage
            </Typography>
          </Box>
          
          <CardContent sx={{ p: 0 }}>
            {error && (
              <Alert severity="error" sx={{ m: 2 }}>
                {error}
              </Alert>
            )}
            
            {organisations.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No organisations available
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {organisations.map((org, index) => (
                  <React.Fragment key={org.id}>
                    {index > 0 && <Divider />}
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => handleSelectOrganisation(org.id)}
                        sx={{
                          py: 2.5,
                          px: 3,
                          '&:hover': {
                            backgroundColor: 'rgba(102, 126, 234, 0.04)'
                          }
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: 'rgba(102, 126, 234, 0.1)',
                              color: '#667eea'
                            }}
                          >
                            <BusinessIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle1" fontWeight={500}>
                              {org.name}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="body2" color="text.secondary">
                              ID: {org.id}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
        
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            You are logged in as an administrator. Select an organisation to continue.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default OrganisationSelector; 